import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../utils/api';
import { setAuthToken, setUserName, removeAuthToken } from '../utils/auth';

export default function AutoLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [error, setError] = useState('');

  useEffect(() => {
    const performAutoLogin = async () => {
      const payload = searchParams.get('payload');
      console.log('[AutoLogin] Starting auto-login flow');
      console.log('[AutoLogin] Payload from URL:', payload ? `${payload.substring(0, 50)}...` : 'NONE');

      // If no payload provided, show error
      if (!payload) {
        console.log('[AutoLogin] ERROR: No payload provided');
        setStatus('error');
        setError('No login payload provided');
        return;
      }

      // Clear any existing session to ensure fresh login with new credentials
      console.log('[AutoLogin] Clearing existing auth token...');
      removeAuthToken();
      console.log('[AutoLogin] Auth token cleared');

      try {
        // Step 1: Decrypt the payload
        console.log('[AutoLogin] Step 1: Decrypting payload...');
        const decryptResponse = await api.post('/parent-portal/decrypt', { payload });
        const { email, consultation_number, student_uuid, course_id } = decryptResponse.data;
        console.log('[AutoLogin] Decrypted - Email:', email, 'Consultation#:', consultation_number, 'Student UUID:', student_uuid, 'Course ID:', course_id);

        // Step 2: Sign in with decrypted credentials
        console.log('[AutoLogin] Step 2: Signing in with credentials...');
        const loginResponse = await api.post('/parent-signin', {
          email,
          consultation_number,
          remember_me: false, // Auto-login uses session cookie (12 hours)
        });

        const { token, expiresIn, userName } = loginResponse.data;
        console.log('[AutoLogin] Login successful - userName:', userName, 'expiresIn:', expiresIn);
        console.log('[AutoLogin] Token (first 50 chars):', token?.substring(0, 50));

        // Step 3: Set auth token and redirect
        console.log('[AutoLogin] Step 3: Setting auth token...');
        setAuthToken(token, expiresIn, false);
        setUserName(userName);
        console.log('[AutoLogin] Auth token and userName set');

        setStatus('success');
        console.log('[AutoLogin] Status set to success, redirecting in 500ms...');

        // Brief delay to show success state, then redirect
        setTimeout(() => {
          // Redirect with student and course pre-selection if provided
          const params = new URLSearchParams();
          if (student_uuid) params.set('student', student_uuid);
          if (course_id) params.set('course', String(course_id));
          const redirectUrl = params.toString() ? `/?${params.toString()}` : '/';
          console.log('[AutoLogin] Redirecting to', redirectUrl);
          navigate(redirectUrl, { replace: true });
        }, 500);
      } catch (err) {
        console.error('[AutoLogin] ERROR:', err);
        console.error('[AutoLogin] Error response:', err.response?.data);
        setStatus('error');
        const serverMessage = err.response?.data?.message;

        // Map server errors to user-friendly messages
        const errorMap = {
          'Link has expired': 'This login link has expired. Please request a new one from your administrator.',
          'Invalid or corrupted payload': 'This login link is invalid. Please request a new one from your administrator.',
          'Email not found': 'Account not found. Please contact your administrator.',
          'Consultation number does not match': 'Account verification failed. Please contact your administrator.',
        };

        const errorMessage = errorMap[serverMessage] || serverMessage || 'Failed to sign in. Please try again or use manual login.';
        console.error('[AutoLogin] Setting error message:', errorMessage);
        setError(errorMessage);
      }
    };

    performAutoLogin();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/50 p-8 transition-colors text-center">
          {status === 'loading' && (
            <>
              <div className="w-16 h-16 mx-auto bg-[#589DD5]/10 rounded-full flex items-center justify-center mb-4">
                <Loader2 className="w-8 h-8 text-[#589DD5] animate-spin" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Signing you in...
              </h1>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Please wait while we verify your credentials
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Success!
              </h1>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Redirecting to your dashboard...
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Unable to Sign In
              </h1>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
                {error}
              </p>
              <button
                onClick={() => navigate('/login', { replace: true })}
                className="w-full bg-[#589DD5] text-white py-3 px-4 rounded-lg hover:bg-[#4a8bc4] focus:outline-none focus:ring-4 focus:ring-[#589DD5]/30 transition-all font-medium"
              >
                Go to Login Page
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
